import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  CalendarBlank,
  CheckSquare,
  Invoice,
  CurrencyGbp,
  ChatsCircle,
  Warning,
  Scales,
  CaretRight,
  Clock,
  CheckCircle,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  fetchNewMatters,
  fetchPendingAllocations,
  fetchUpcomingHearings,
  fetchTasksDueToday,
  fetchOutstandingFeeNotes,
  fetchOverduePayments,
  fetchRequiringAttention,
  fetchUnreadThreads,
  type OpsNewMatter,
  type Allocation,
  type Hearing,
  type Task,
  type FeeNoteListRow,
  type AgedDebtSummary,
  type AttentionItem,
  type MessageThread,
} from '@/lib/clerkOpsApi';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function daysBetween(from: string, to: Date = new Date()) {
  return Math.floor((to.getTime() - new Date(from).getTime()) / 86_400_000);
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);
}


const ATTENTION_LABELS: Record<string, string> = {
  missing_outcome: 'No outcome',
  overdue_tasks:   'Tasks overdue',
  stale:           'No activity',
};

// ─── Empty state ─────────────────────────────────────────────────────────────

function AllClear({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" weight="fill" />
      <span>{label}</span>
    </div>
  );
}

// ─── Section skeleton ─────────────────────────────────────────────────────────

function SectionLoading() {
  return (
    <div className="space-y-2 py-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-9 rounded bg-muted/40 animate-pulse" />
      ))}
    </div>
  );
}

// ─── Row components ───────────────────────────────────────────────────────────

function MatterRow({ m, basePath }: { m: OpsNewMatter; basePath: string }) {
  return (
    <Link
      to={`${basePath}/matters/${m.id}`}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{m.caseName}</span>
        <span className="text-xs text-muted-foreground">{m.caseNumber}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {m.practiceArea && (
          <span className="text-xs text-muted-foreground hidden sm:inline">{m.practiceArea.name}</span>
        )}
        <CaretRight className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </Link>
  );
}

function AllocationRow({ a, basePath }: { a: Allocation; basePath: string }) {
  const days = daysBetween(a.offeredAt);
  return (
    <Link
      to={`${basePath}/matters/${a.case.id}`}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{a.case.caseName}</span>
        <span className="text-xs text-muted-foreground">{a.case.caseNumber}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        <span>{days === 0 ? 'today' : `${days}d`}</span>
        {a.respondByDate && (
          <span className={daysBetween(a.respondByDate) > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
            · by {formatDate(a.respondByDate)}
          </span>
        )}
      </div>
    </Link>
  );
}

function HearingRow({ h, basePath }: { h: Hearing; basePath: string }) {
  return (
    <Link
      to={`${basePath}/hearings/${h.id}`}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{h.matterName}</span>
        <span className="text-xs text-muted-foreground">{h.type} · {h.court}</span>
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">
        {formatDate(h.date)}
      </div>
    </Link>
  );
}

function TaskRow({ t, basePath }: { t: Task; basePath: string }) {
  const linkTo = t.caseId
    ? `${basePath}/matters/${t.caseId}`
    : `${basePath}/tasks`;
  return (
    <Link
      to={linkTo}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{t.title}</span>
        {t.case && (
          <span className="text-xs text-muted-foreground">{t.case.caseNumber} · {t.case.caseName}</span>
        )}
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">
        {t.dueDate ? formatDate(t.dueDate) : '—'}
      </div>
    </Link>
  );
}

function FeeNoteRow({ fn, basePath }: { fn: FeeNoteListRow; basePath: string }) {
  const isOverdue = fn.status === 'overdue' || (fn.dueDate && new Date(fn.dueDate) < new Date());
  return (
    <Link
      to={`${basePath}/fees`}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">
          {fn.case?.caseName ?? fn.feeNoteNumber}
        </span>
        <span className="text-xs text-muted-foreground">{fn.feeNoteNumber}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-medium tabular-nums ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
          £{Number(fn.total).toLocaleString('en-GB')}
        </span>
        {fn.dueDate && (
          <span className="text-xs text-muted-foreground">by {formatDate(fn.dueDate)}</span>
        )}
      </div>
    </Link>
  );
}

function AgedDebtTable({ summary }: { summary: AgedDebtSummary }) {
  if (summary.overdueRows.length === 0) {
    return <AllClear label="No overdue balances" />;
  }
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-4 gap-2 px-1 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b">
        <span>Client</span>
        <span className="text-right">30 d</span>
        <span className="text-right">60 d</span>
        <span className="text-right">90+ d</span>
      </div>
      {summary.overdueRows.slice(0, 8).map((r) => (
        <div key={r.contactId ?? r.clientName} className="grid grid-cols-4 gap-2 px-1 py-1.5 text-sm rounded hover:bg-muted/50">
          <span className="truncate font-medium">{r.clientName}</span>
          <span className={`text-right tabular-nums text-xs ${r.d30 > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
            {r.d30 > 0 ? formatCurrency(r.d30) : '—'}
          </span>
          <span className={`text-right tabular-nums text-xs ${r.d60 > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-muted-foreground'}`}>
            {r.d60 > 0 ? formatCurrency(r.d60) : '—'}
          </span>
          <span className={`text-right tabular-nums text-xs ${r.d90 > 0 ? 'text-red-700 dark:text-red-300 font-semibold' : 'text-muted-foreground'}`}>
            {r.d90 > 0 ? formatCurrency(r.d90) : '—'}
          </span>
        </div>
      ))}
      {summary.totals.total > 0 && (
        <div className="grid grid-cols-4 gap-2 px-1 pt-2 border-t text-xs font-semibold text-foreground">
          <span>Total overdue</span>
          <span className="text-right tabular-nums">{formatCurrency(summary.totals.d30)}</span>
          <span className="text-right tabular-nums">{formatCurrency(summary.totals.d60)}</span>
          <span className="text-right tabular-nums text-red-700 dark:text-red-300">{formatCurrency(summary.totals.d90)}</span>
        </div>
      )}
    </div>
  );
}

function AttentionRow({ item, basePath }: { item: AttentionItem; basePath: string }) {
  return (
    <Link
      to={`${basePath}/matters/${item.matterId}`}
      className="flex items-start justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{item.caseName}</span>
        <span className="text-xs text-muted-foreground">{item.detail}</span>
      </div>
      {/* `flex-wrap` with `shrink-0` never wraps: the group keeps its content width — three
          reason badges came to 300px — and hangs off the right of a narrow phone. It is the
          matter name beside it that must give way, and it already can (`min-w-0` + `truncate`). */}
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
        {item.reasons.map((r) => (
          <Badge
            key={r}
            variant="outline"
            className={`text-xs font-normal ${
              r === 'missing_outcome' ? 'border-red-300 dark:border-red-500/40 text-red-700 dark:text-red-300' :
              r === 'overdue_tasks'   ? 'border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300' :
              'border-border text-muted-foreground'
            }`}
          >
            {ATTENTION_LABELS[r]}
          </Badge>
        ))}
      </div>
    </Link>
  );
}

function ThreadRow({ t, basePath }: { t: MessageThread; basePath: string }) {
  const last = t.messages?.[0];
  return (
    <Link
      to={`${basePath}/communications`}
      state={{ highlightThreadId: t.id }}
      className="flex items-center justify-between gap-3 rounded px-1 py-2 text-sm hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0">
        <span className="font-medium truncate block">{t.subject ?? '(no subject)'}</span>
        {last?.senderUser && (
          <span className="text-xs text-muted-foreground">
            {last.senderUser.firstName} {last.senderUser.lastName}
            {t.case && ` · ${t.case.caseNumber}`}
          </span>
        )}
      </div>
      <Badge variant="default" className="shrink-0 tabular-nums text-xs">
        {t.unreadCount}
      </Badge>
    </Link>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  count?: number;
  isLoading: boolean;
  /** A failed load must not render the section's "nothing here" message. */
  isError?: boolean;
  onRetry?: () => void;
  children: React.ReactNode;
}

function SectionCard({ icon, title, count, isLoading, isError, onRetry, children }: SectionCardProps) {
  return (
    <Card className="shadow-none border">
      <CardHeader className="px-4 pt-4 pb-2">
        <CardTitle className="flex items-center justify-between text-sm font-semibold text-foreground">
          <span className="flex items-center gap-2">
            {icon}
            {title}
          </span>
          {count !== undefined && count > 0 && (
            <Badge variant="default" className="tabular-nums text-xs font-medium">
              {count}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {isLoading ? <SectionLoading /> : isError ? <QueryErrorState compact what={title.toLowerCase()} onRetry={onRetry} /> : children}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OperationsCenterPage() {
  const user = useAuthStore((s) => s.user);
  const basePath = '/clerk';

  const newMattersQ = useQuery({
    queryKey: ['ops-new-matters'],
    queryFn: fetchNewMatters,
    staleTime: 60_000,
  });

  const pendingAllocsQ = useQuery({
    queryKey: ['ops-pending-allocs'],
    queryFn: fetchPendingAllocations,
    staleTime: 30_000,
  });

  const hearingsQ = useQuery({
    queryKey: ['ops-upcoming-hearings'],
    queryFn: fetchUpcomingHearings,
    staleTime: 60_000,
  });

  const tasksQ = useQuery({
    queryKey: ['ops-tasks-today', user?.id],
    queryFn: () => fetchTasksDueToday(user!.id),
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const feeNotesQ = useQuery({
    queryKey: ['ops-fee-notes'],
    queryFn: fetchOutstandingFeeNotes,
    staleTime: 120_000,
  });

  const agedDebtQ = useQuery({
    queryKey: ['ops-aged-debt'],
    queryFn: fetchOverduePayments,
    staleTime: 120_000,
  });

  const attentionQ = useQuery({
    queryKey: ['ops-requiring-attention'],
    queryFn: fetchRequiringAttention,
    staleTime: 60_000,
  });

  const threadsQ = useQuery({
    queryKey: ['ops-unread-threads'],
    queryFn: fetchUnreadThreads,
    staleTime: 30_000,
  });

  const newMatters    = newMattersQ.data   ?? [];
  const pendingAllocs = pendingAllocsQ.data ?? [];
  const hearings      = hearingsQ.data     ?? [];
  const tasks         = tasksQ.data        ?? [];
  const feeNotes      = feeNotesQ.data     ?? [];
  const threads       = threadsQ.data      ?? [];
  const attention     = attentionQ.data    ?? [];

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <PageHeader title="Operations Centre" subtitle="Live operational view across all active matters" />

      {/* Row 1: New Instructions · Pending Allocations · Hearings · Tasks */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* 1. New Matters */}
        <SectionCard
          icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
          title="New Instructions"
          count={newMatters.length}
          isLoading={newMattersQ.isLoading}
          isError={newMattersQ.isError}
          onRetry={() => newMattersQ.refetch()}
        >
          {newMatters.length === 0 ? (
            <AllClear label="No new instructions in the last 48 hours" />
          ) : (
            <div className="divide-y">
              {newMatters.slice(0, 6).map((m) => (
                <MatterRow key={m.id} m={m} basePath={basePath} />
              ))}
              {newMatters.length > 6 && (
                <Link to={`${basePath}/matters`} className="block px-1 py-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                  +{newMatters.length - 6} more
                </Link>
              )}
            </div>
          )}
        </SectionCard>

        {/* 2. Awaiting Barrister Response */}
        <SectionCard
          icon={<Scales className="h-4 w-4 text-muted-foreground" />}
          title="Awaiting Response"
          count={pendingAllocs.length}
          isLoading={pendingAllocsQ.isLoading}
          isError={pendingAllocsQ.isError}
          onRetry={() => pendingAllocsQ.refetch()}
        >
          {pendingAllocs.length === 0 ? (
            <AllClear label="No pending brief offers" />
          ) : (
            <div className="divide-y">
              {pendingAllocs.slice(0, 6).map((a) => (
                <AllocationRow key={a.id} a={a} basePath={basePath} />
              ))}
            </div>
          )}
        </SectionCard>

        {/* 3. Hearings Next 7 Days */}
        <SectionCard
          icon={<CalendarBlank className="h-4 w-4 text-muted-foreground" />}
          title="Hearings — 7 Days"
          count={hearings.length}
          isLoading={hearingsQ.isLoading}
          isError={hearingsQ.isError}
          onRetry={() => hearingsQ.refetch()}
        >
          {hearings.length === 0 ? (
            <AllClear label="No confirmed hearings this week" />
          ) : (
            <div className="divide-y">
              {hearings.slice(0, 6).map((h) => (
                <HearingRow key={h.id} h={h} basePath={basePath} />
              ))}
              {hearings.length > 6 && (
                <Link to={`${basePath}/hearings`} className="block px-1 py-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                  +{hearings.length - 6} more
                </Link>
              )}
            </div>
          )}
        </SectionCard>

        {/* 4. Tasks Due Today */}
        <SectionCard
          icon={<CheckSquare className="h-4 w-4 text-muted-foreground" />}
          title="Tasks Due Today"
          count={tasks.length}
          isLoading={tasksQ.isLoading}
          isError={tasksQ.isError}
          onRetry={() => tasksQ.refetch()}
        >
          {tasks.length === 0 ? (
            <AllClear label="No tasks due today" />
          ) : (
            <div className="divide-y">
              {tasks.slice(0, 6).map((t) => (
                <TaskRow key={t.id} t={t} basePath={basePath} />
              ))}
              {tasks.length > 6 && (
                <Link to={`${basePath}/tasks`} className="block px-1 py-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                  +{tasks.length - 6} more
                </Link>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Row 2: Fee Notes + Aged Debt — side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 5. Outstanding Fee Notes */}
        <SectionCard
          icon={<Invoice className="h-4 w-4 text-muted-foreground" />}
          title="Outstanding Fee Notes"
          count={feeNotes.length}
          isLoading={feeNotesQ.isLoading}
          isError={feeNotesQ.isError}
          onRetry={() => feeNotesQ.refetch()}
        >
          {feeNotes.length === 0 ? (
            <AllClear label="No outstanding fee notes" />
          ) : (
            <div className="divide-y">
              {feeNotes.slice(0, 6).map((fn) => (
                <FeeNoteRow key={fn.id} fn={fn} basePath={basePath} />
              ))}
              {feeNotes.length > 6 && (
                <Link to={`${basePath}/fees`} className="block px-1 py-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                  +{feeNotes.length - 6} more
                </Link>
              )}
            </div>
          )}
        </SectionCard>

        {/* 6. Overdue Payments */}
        <SectionCard
          icon={<CurrencyGbp className="h-4 w-4 text-muted-foreground" />}
          title="Overdue Payments"
          isLoading={agedDebtQ.isLoading}
          isError={agedDebtQ.isError}
          onRetry={() => agedDebtQ.refetch()}
        >
          {agedDebtQ.data ? (
            <AgedDebtTable summary={agedDebtQ.data} />
          ) : null}
        </SectionCard>
      </div>

      {/* Row 3: Matters Requiring Attention — full width */}
      <SectionCard
        icon={<Warning className="h-4 w-4 text-muted-foreground" />}
        title="Matters Requiring Attention"
        count={attention.length}
        isLoading={attentionQ.isLoading}
        isError={attentionQ.isError}
        onRetry={() => attentionQ.refetch()}
      >
        {attention.length === 0 ? (
          <AllClear label="All matters are in order" />
        ) : (
          <div className="grid grid-cols-1 divide-y sm:grid-cols-2 sm:divide-y-0 sm:gap-x-6">
            {attention.slice(0, 12).map((item) => (
              <AttentionRow key={item.matterId} item={item} basePath={basePath} />
            ))}
          </div>
        )}
        {attention.length > 12 && (
          <Link to={`${basePath}/matters`} className="block mt-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
            +{attention.length - 12} more matters need attention
          </Link>
        )}
      </SectionCard>

      {/* Row 4: Unread Communications */}
      <SectionCard
        icon={<ChatsCircle className="h-4 w-4 text-muted-foreground" />}
        title="Unread Communications"
        count={threads.length}
        isLoading={threadsQ.isLoading}
        isError={threadsQ.isError}
        onRetry={() => threadsQ.refetch()}
      >
        {threads.length === 0 ? (
          <AllClear label="No unread messages" />
        ) : (
          <div className="grid grid-cols-1 divide-y sm:grid-cols-2 sm:divide-y-0 sm:gap-x-6">
            {threads.slice(0, 8).map((t) => (
              <ThreadRow key={t.id} t={t} basePath={basePath} />
            ))}
          </div>
        )}
        {threads.length > 8 && (
          <Link to={`${basePath}/communications`} className="block mt-2 text-xs text-brand-600 dark:text-brand-400 hover:underline">
            +{threads.length - 8} more unread threads
          </Link>
        )}
      </SectionCard>
    </div>
  );
}
